import { memo, Ref, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  AudioPlayer,
  Button,
  InfoTooltipModal,
  Pill,
  Stack,
  VideoPlayerHandle,
} from 'src/components';
import {
  isCharacterLabel,
  isLiteralTranslationLabel,
  isRomanizationLabel,
} from 'src/api/settings/learningToggles';
import { MemriseError } from 'src/utils/errors';
import { randomInteger } from 'src/utils/numbers';
import { LearnableRowInfo, LearnableValueKind } from 'src/api/learningSessions/types';
import {
  formatArabicBreakdown,
  shouldShowArabicBreakdown,
} from 'src/utils/language/arabicBreakdown';
import { useShowAlternatives } from 'src/hooks/useShowAlternatives';
import { SLOW_VIDEO_SPEED } from 'src/utils/videoSpeed';
import { romanizationTextAttrs } from 'src/utils/pinyinFont';
import { Alert } from '../../Alert';
import { PitchAccent } from '../PitchAccent';
import { resolveKanaReading } from '../PitchAccent/utils';
import { FullscreenBackgroundVideo } from './FullscreenBackgroundVideo';
import { PronunciationPractice } from './PronunciationPractice';
import * as S from './styles';

const msgs = defineMessages({
  audio: {
    id: 'learning_session_learnables_card_audio',
    defaultMessage: 'Audio',
  },
  attributes: {
    id: 'learning_session_learnable_card_attributes',
    defaultMessage: 'Attributes',
  },
  yourAnswer: {
    id: 'learning_session_previous_incorrect_answer_title',
    defaultMessage: 'Your answer',
  },
  addedFromSaved: { id: 'blob_session_added_from_saved', defaultMessage: 'Added from Saved' },
  actuallyIWasRight: {
    id: 'learning_session_actually_i_was_right',
    defaultMessage: 'Actually, I was right!',
  },
  actuallyIWasRightTooltip: {
    id: 'learning_session_actually_i_was_right_tooltip',
    defaultMessage:
      'If you mistakenly pressed the wrong button, but actually you knew the answer, press the "Actually I was right!" button, so that Memrise forgets about the mistake. Also useful when Memrise is a bit too strict with you.',
  },
  showLetters: {
    id: 'learning_session_arabic_breakdown_show_button',
    defaultMessage: 'Show letter breakdown',
  },
  hideLetters: {
    id: 'learning_session_arabic_breakdown_hide_button',
    defaultMessage: 'Hide letter breakdown',
  },
});

interface ArabicBreakdownProps {
  text: string;
}

const ArabicBreakdown = ({ text }: ArabicBreakdownProps) => {
  const intl = useIntl();
  const [isVisible, setIsVisible] = useState(false);

  return (
    <S.LetterBreakdownContainer>
      <Button buttonSize="small" buttonType="secondary" onClick={() => setIsVisible(!isVisible)}>
        {intl.formatMessage(isVisible ? msgs.hideLetters : msgs.showLetters)}
      </Button>
      {isVisible && (
        <S.LetterBreakdownText dir="auto" data-testid="arabicBreakdownText">
          {text}
        </S.LetterBreakdownText>
      )}
    </S.LetterBreakdownContainer>
  );
};

interface LearnableRowProps extends LearnableRowInfo {
  size: 'primary' | 'secondary' | 'tertiary';
  autoPlay?: boolean;
  onAutoPlayEnded?: () => void;
}

export const LearnableRow = ({
  label,
  value,
  alternatives,
  kind,
  size,
  autoPlay = false,
  onAutoPlayEnded,
  showBigger = false,
}: LearnableRowProps) => {
  const intl = useIntl();
  const { showAlternatives, toggleShowAlternatives } = useShowAlternatives(label);

  // This error throwing behaviour *should* be captured by type info.
  // However, this comes from the API, so we double-check at runtime
  // to ensure that our assumptions are correct.
  if (kind === LearnableValueKind.VIDEO) {
    throw new MemriseError('Learnable Row cannot be Video');
  }

  const hasAlternatives =
    kind === LearnableValueKind.TEXT &&
    size !== 'tertiary' &&
    !!alternatives &&
    alternatives.length > 0;
  const romanizationProps = isRomanizationLabel(label) ? romanizationTextAttrs(value) : undefined;

  return (
    <div>
      {hasAlternatives ? (
        <S.Label
          as="button"
          type="button"
          $clickable
          onClick={toggleShowAlternatives}
          aria-pressed={showAlternatives}
        >
          {label}
        </S.Label>
      ) : (
        <S.Label htmlFor="itemValue">{label}</S.Label>
      )}
      {kind === LearnableValueKind.TEXT ? (
        <>
          {size === 'primary' ? (
            <S.H2Value
              $showBigger={showBigger}
              dir="auto"
              data-testid="learnableRowTextPrimary"
              {...romanizationProps}
            >
              {value}
            </S.H2Value>
          ) : size === 'secondary' ? (
            <S.H3Value
              $showBigger={showBigger}
              dir="auto"
              data-testid="learnableRowTextSecondary"
              {...romanizationProps}
            >
              {value}
            </S.H3Value>
          ) : (
            <S.H4Value
              $showBigger={showBigger}
              dir="auto"
              data-testid="learnableRowText"
              {...romanizationProps}
            >
              {value}
            </S.H4Value>
          )}
          {hasAlternatives && showAlternatives && alternatives && (
            <S.TextAlternatives>{alternatives.join(' • ')}</S.TextAlternatives>
          )}
        </>
      ) : kind === LearnableValueKind.IMAGE ? (
        <S.Image data-testid="learnableRowImg" src={value} alt="" />
      ) : (
        <S.AudioContainer>
          <AudioPlayer
            ariaText={intl.formatMessage(msgs.audio)}
            src={value}
            autoPlay={autoPlay}
            onEnded={autoPlay ? onAutoPlayEnded : undefined}
            // only autoplaying audio should replay on keyboard shortcut
            playOnKeyboardShortcut={autoPlay}
          />
        </S.AudioContainer>
      )}
    </div>
  );
};

export interface PresentationLearnableCardProps {
  item: LearnableRowInfo;
  definition: LearnableRowInfo;
  attributeValues: string[];

  learnableId?: number;
  languagePairId?: number;

  /** Array of urls to audio files */
  audioSrcs?: string[];

  /** Url to .mp4 file */
  videoSrc: string | null;

  /**
   * Formerly known as hidden_info (now we don't hide it)
   */
  extraInfo?: LearnableRowInfo[];

  previousIncorrectAnswer: string | null;

  videoAutoPlay?: boolean;

  /** Callback to mark the previous incorrect answer as actually correct */
  onMarkPreviousAnswerCorrect?: () => void;

  showLiteralTranslation?: boolean;
  showRomanization?: boolean;
  showSlowAudio?: boolean;
  showArabicBreakdown?: boolean;
  showPitchAccent?: boolean;
  pitchAccent?: number[] | null;
  kana?: string | null;
  showPhoneticSpelling?: boolean;
  phoneticSpelling?: string | null;
  onInitialMediaEnded?: () => void;
  videoControlRef?: Ref<PresentationVideoControl>;
  onPlayingSlowlyChange?: (isPlayingSlowly: boolean) => void;
  isFromSaved?: boolean;
}

export type PresentationVideoControl = {
  play: () => void;
  playSlow: () => void;
};

export type AutoPlayLocation = 'video' | 'primaryAudio' | 'secondaryAudio' | 'extraAudio' | 'none';

export const getAutoPlayLocation = (
  hasVideoSrc: boolean,
  hasAudioSrc: boolean,
  itemKind: LearnableValueKind,
  definitionKind: LearnableValueKind,
): AutoPlayLocation => {
  return hasVideoSrc
    ? 'video'
    : itemKind === LearnableValueKind.AUDIO
      ? 'primaryAudio'
      : definitionKind === LearnableValueKind.AUDIO
        ? 'secondaryAudio'
        : hasAudioSrc
          ? 'extraAudio'
          : 'none';
};

export const shouldPlayAudioSrc = (
  autoPlayLocation: AutoPlayLocation,
  chosenAudioIndex: number,
  i: number,
) => chosenAudioIndex === i && autoPlayLocation === 'extraAudio';

const PresentationLearnableCard = ({
  item,
  definition,
  attributeValues,
  learnableId,
  languagePairId,
  audioSrcs,
  videoSrc,
  extraInfo,
  previousIncorrectAnswer,
  videoAutoPlay = true,
  onMarkPreviousAnswerCorrect,
  showLiteralTranslation = true,
  showRomanization = true,
  showSlowAudio: _showSlowAudio = true,
  showArabicBreakdown = true,
  showPitchAccent = true,
  pitchAccent,
  kana,
  showPhoneticSpelling = true,
  phoneticSpelling,
  onInitialMediaEnded,
  videoControlRef,
  onPlayingSlowlyChange,
  isFromSaved = false,
}: PresentationLearnableCardProps) => {
  const intl = useIntl();
  const literalTranslationLabel = intl.formatMessage({ id: 'literal_translation' });
  const videoPlayerRef = useRef<VideoPlayerHandle>(null);

  // VideoPlayer rebuilds its autoplay effect whenever onPlay changes identity, which
  // restarts the video, so this callback has to stay stable across renders.
  const handleVideoPlay = useCallback(() => {
    onPlayingSlowlyChange?.(false);
  }, [onPlayingSlowlyChange]);

  const handleVideoEnded = useCallback(() => {
    onPlayingSlowlyChange?.(false);
    onInitialMediaEnded?.();
  }, [onInitialMediaEnded, onPlayingSlowlyChange]);

  useImperativeHandle(
    videoControlRef,
    () => ({
      play: () => {
        onPlayingSlowlyChange?.(false);
        videoPlayerRef.current?.replayAtSpeed(1);
      },
      playSlow: () => {
        onPlayingSlowlyChange?.(true);
        videoPlayerRef.current?.replayAtSpeed(SLOW_VIDEO_SPEED);
      },
    }),
    [onPlayingSlowlyChange],
  );

  const arabicBreakdownText = useMemo(
    () =>
      showArabicBreakdown && shouldShowArabicBreakdown(item.value)
        ? formatArabicBreakdown(item.value)
        : null,
    [showArabicBreakdown, item.value],
  );

  const pitchAccentReading = useMemo(
    () => resolveKanaReading(kana, extraInfo, item.value),
    [kana, extraInfo, item.value],
  );

  const showPitchAccentDiagram = Boolean(
    showPitchAccent && pitchAccent?.length && pitchAccentReading,
  );

  const filteredExtraInfo = useMemo(
    () =>
      extraInfo?.filter(info => {
        if (
          !showLiteralTranslation &&
          isLiteralTranslationLabel(info.label, literalTranslationLabel)
        ) {
          return false;
        }

        if (!showRomanization && isRomanizationLabel(info.label)) {
          return false;
        }

        if (isCharacterLabel(info.label) && info.value === item.value) {
          return false;
        }

        return true;
      }),
    [extraInfo, item.value, literalTranslationLabel, showLiteralTranslation, showRomanization],
  );

  const autoPlayLocation = getAutoPlayLocation(
    !!videoSrc,
    !!audioSrcs && audioSrcs.length > 0,
    item?.kind,
    definition?.kind,
  );

  const chosenAudioIndex = useMemo(
    () => randomInteger({ min: 0, max: audioSrcs ? audioSrcs.length : 0 }),
    [audioSrcs],
  );

  /* istanbul ignore next: This should never happen */
  if (!item.value || !definition.value) {
    throw new MemriseError('Item or definition missing');
  }

  return (
    <S.Container data-testid="presentationLearnableCard">
      {/* VideoPlayer autoplays by default, so we don't need to check
            the autoPlayLocation here */}
      {videoSrc && (
        <FullscreenBackgroundVideo
          ref={videoPlayerRef}
          src={videoSrc}
          autoPlay={videoAutoPlay}
          data-testid="presentationVideoContainer"
          overlayTestId="presentationVideoOverlay"
          onPlay={handleVideoPlay}
          onEnded={handleVideoEnded}
        />
      )}
      <S.InfoContainer $onVideo={!!videoSrc} data-testid="presentationInfoContainer">
        <Stack>
          {previousIncorrectAnswer !== null && previousIncorrectAnswer.length > 0 && (
            <Alert level="danger" title={intl.formatMessage(msgs.yourAnswer)}>
              <S.IncorrectAnswerContent>
                <S.IncorrectAnswerText>{previousIncorrectAnswer}</S.IncorrectAnswerText>
                <S.ActuallyRightContainer>
                  <Button
                    buttonSize="small"
                    buttonType="secondary"
                    onClick={() => {
                      onMarkPreviousAnswerCorrect?.();
                    }}
                  >
                    {intl.formatMessage(msgs.actuallyIWasRight)}
                  </Button>
                  <InfoTooltipModal tooltip={intl.formatMessage(msgs.actuallyIWasRightTooltip)} />
                </S.ActuallyRightContainer>
              </S.IncorrectAnswerContent>
            </Alert>
          )}
          {isFromSaved && (
            <S.SavedBadge data-testid="presentation-saved-badge">
              <S.SavedIcon name="bookmarkFilled" />
              {intl.formatMessage(msgs.addedFromSaved)}
            </S.SavedBadge>
          )}
          <Stack $spacing="s1">
            <LearnableRow
              size="primary"
              autoPlay={autoPlayLocation === 'primaryAudio'}
              onAutoPlayEnded={onInitialMediaEnded}
              key={`item-${item.value}`}
              {...item}
            />
            {showPhoneticSpelling && phoneticSpelling && item.kind === LearnableValueKind.TEXT && (
              <S.PhoneticSpelling data-testid="phoneticSpelling">
                {phoneticSpelling}
              </S.PhoneticSpelling>
            )}
          </Stack>
          {arabicBreakdownText && (
            <ArabicBreakdown key={`arabic-breakdown-${item.value}`} text={arabicBreakdownText} />
          )}
          <LearnableRow
            size="secondary"
            autoPlay={autoPlayLocation === 'secondaryAudio'}
            onAutoPlayEnded={onInitialMediaEnded}
            key={`def-${definition.value}`}
            {...definition}
          />
          {showPitchAccentDiagram ? (
            <PitchAccent accents={pitchAccent} reading={pitchAccentReading} />
          ) : null}
          {!videoSrc && <S.Separator />}
          {filteredExtraInfo &&
            filteredExtraInfo.length > 0 &&
            filteredExtraInfo.map(info => (
              <LearnableRow key={info.value + '-' + info.label} size="tertiary" {...info} />
            ))}
          {attributeValues.length > 0 && (
            <div>
              <S.Label>{intl.formatMessage(msgs.attributes)}</S.Label>
              <S.AttributesContainer>
                {attributeValues.map((attributeValue, attributeIndex) => {
                  return (
                    <S.Attribute
                      // eslint-disable-next-line react/no-array-index-key
                      key={`attribute-${attributeValue}-${attributeIndex}`}
                    >
                      <Pill color="green">{attributeValue}</Pill>
                    </S.Attribute>
                  );
                })}
              </S.AttributesContainer>
            </div>
          )}
          {audioSrcs && audioSrcs.length > 0 && !videoSrc && (
            <div>
              <S.Label>{intl.formatMessage(msgs.audio)}</S.Label>
              <S.AudioContainer>
                {audioSrcs.map((src, i) => {
                  const shouldPlay = shouldPlayAudioSrc(autoPlayLocation, chosenAudioIndex, i);
                  return (
                    <AudioPlayer
                      ariaText={intl.formatMessage(msgs.audio) + ' - ' + (i + 1)}
                      key={src}
                      src={src}
                      autoPlay={shouldPlay}
                      onEnded={shouldPlay ? onInitialMediaEnded : undefined}
                      // only autoplaying audio should replay on keyboard shortcut
                      playOnKeyboardShortcut={shouldPlay}
                    />
                  );
                })}
              </S.AudioContainer>
            </div>
          )}
          {learnableId && languagePairId && !videoSrc && (
            <PronunciationPractice
              key={learnableId}
              learnableId={learnableId}
              languagePairId={languagePairId}
              hasVideo={false}
            />
          )}
        </Stack>
      </S.InfoContainer>
    </S.Container>
  );
};

export default memo(PresentationLearnableCard);
